// @flow

import * as React from 'react';
import styled from 'styled-components';
import Handle from './Handle';
import Fill from './Fill';
import keycode from 'keycode';

type Props = {
  id: string,
  value: number,
  min: number,
  max: number,
  step: number,
  bigStep: number,
  onChange: (newValue: number) => void,
  onMoveStart?: () => void,
  onMoveEnd?: () => void,
  disabled?: boolean,
  handleComponent?: React.ComponentType<handleProps>,
  fillComponent?: React.ComponentType<fillProps>,
};

type handleProps = {
  position?: number,
  sliderWidth?: number,
  active?: boolean,
};

type fillProps = {
  end?: number,
};

type State = {
  value: number,
  width: number,
  offset: number,
  active: boolean,
};

const SliderContainer = styled.div`
  margin: 1.125em 0;
  height: 4px;
  opacity: 0.8;
  border-radius: 100px;
  background-color: ${(props) => props.theme.slider.backgroundColor};
  position: relative;
  touch-action: none;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.25 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  flex-grow: 1;
`;

export default class Slider extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      value: Math.max(props.value, props.min),
      width: 0,
      offset: 0,
      active: false,
    };
  }

  static defaultProps = {
    value: 0,
    min: 0,
    max: 100,
    step: 1,
    bigStep: 5,
    onChange: function () {},
  };

  ref: HTMLDivElement;

  /**** Lifecycle events ****/

  componentDidMount() {
    this.initDimensions();
    window.addEventListener('resize', this.handleResize);
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    if (newProps.value !== this.props.value) {
      this.setValue(newProps.value);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  /**** General Event handlers ****/

  handleNoop = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
  };

  handleChange = (newValue: number) => {
    if (newValue !== this.state.value) {
      this.setState({ value: newValue });
    }
    this.props.onChange(newValue);
  };

  handleMoveStart = () => {
    this.setState({ active: true });
    if (
      this.props.onMoveStart !== null &&
      this.props.onMoveStart !== undefined
    ) {
      this.props.onMoveStart();
    }
  };

  handleMoveEnd = () => {
    this.setState({ active: false });
    if (this.props.onMoveEnd !== null && this.props.onMoveEnd !== undefined) {
      this.props.onMoveEnd();
    }
  };

  handleResize = () => {
    this.initDimensions();
  };

  /**** Mouse Event handlers ****/

  handleMouseDown = (e: SyntheticMouseEvent<HTMLDivElement>) => {
    if (this.props.disabled) return;
    this.handleMouseMove(e);
    document.addEventListener('mouseup', this.handleMouseUp);
    document.addEventListener('mousemove', this.handleMouseMove);
    this.handleMoveStart();
  };

  handleMouseUp = (e: MouseEvent) => {
    this.handleNoop(e);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('mousemove', this.handleMouseMove);
    this.handleMoveEnd();
  };

  handleMouseMove = (e: MouseEvent | SyntheticMouseEvent<HTMLDivElement>) => {
    this.handleNoop(e);
    const position = this.getPositionFromMouseCursor(e);
    const newValue = this.getValueFromPosition(position);
    this.setValue(newValue);
  };

  /**** Touch Event handlers ****/

  handleTouchStart = (e: SyntheticTouchEvent<HTMLDivElement>) => {
    if (this.props.disabled) return;
    this.handleTouchMove(e);
    document.addEventListener('touchend', this.handleTouchEnd);
    document.addEventListener('touchmove', this.handleTouchMove);
    this.handleMoveStart();
  };

  handleTouchEnd = (e: TouchEvent) => {
    this.handleNoop(e);
    document.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('touchmove', this.handleTouchMove);
    this.handleMoveEnd();
  };

  handleTouchMove = (e: TouchEvent | SyntheticTouchEvent<HTMLDivElement>) => {
    this.handleNoop(e);
    const position = this.getPositionFromTouchCursor(e);
    const newValue = this.getValueFromPosition(position);
    this.setValue(newValue);
  };

  /**** Keyboard Event handlers ****/

  handleKeyDown = (e: KeyboardEvent) => {
    if (this.props.disabled) return;
    let newVal = this.state.value;
    switch (e.keyCode) {
      case keycode('up'):
      case keycode('right'):
        newVal = this.state.value + this.props.step;
        break;
      case keycode('down'):
      case keycode('left'):
        newVal = this.state.value - this.props.step;
        break;
      case keycode('end'):
        newVal = this.props.max;
        break;
      case keycode('home'):
        newVal = this.props.min;
        break;
      case keycode('page up'):
        newVal = this.state.value + this.props.bigStep;
        break;
      case keycode('page down'):
        newVal = this.state.value - this.props.bigStep;
        break;
      default:
        break;
    }
    if (newVal !== this.state.value) {
      this.setValue(newVal);
    }
  };

  /**** Helpers ****/

  getWidth() {
    if (!this.ref) return 0;
    return this.ref.offsetWidth;
  }

  getOffset() {
    if (!this.ref) return 0;
    return this.ref.getBoundingClientRect().left;
  }

  getPositionFromMouseCursor(
    e: MouseEvent | SyntheticMouseEvent<HTMLDivElement>
  ) {
    let handlePos = 0;
    if (typeof e.pageX !== 'undefined') {
      handlePos = e.pageX;
    } else if (e && typeof e.clientX !== 'undefined') {
      handlePos = e.clientX;
    }
    return handlePos - this.state.offset;
  }

  getPositionFromTouchCursor(
    e: TouchEvent | SyntheticTouchEvent<HTMLDivElement>
  ) {
    let handlePos = 0;
    if (
      e.touches &&
      e.touches[0] &&
      typeof e.touches[0].clientX !== 'undefined'
    ) {
      handlePos = e.touches[0].clientX;
    }
    return handlePos - this.state.offset;
  }

  getPositionFromValue() {
    const { min, max } = this.props;
    return ((this.state.value - min) / (max - min)) * 100;
  }

  getValueFromPosition(position: number) {
    const { min, max } = this.props;
    const ratio = (max - min) / this.state.width;
    return position * ratio + min;
  }

  setValue(newValue: number) {
    const value = this.snapToStep(newValue);
    this.handleChange(value);
  }

  initDimensions() {
    this.setState({
      width: this.getWidth(),
      offset: this.getOffset(),
    });
  }

  snapToStep(value: number) {
    const { min, max, step } = this.props;
    const remainder = value % step;
    const prevNumber = Math.max(value - remainder, min);
    const nextNumber = Math.min(prevNumber + step, max);
    return value - prevNumber >= nextNumber - value ? nextNumber : prevNumber;
  }

  /**** Render ****/

  render() {
    const position = this.getPositionFromValue();
    const FillComponent = this.props.fillComponent
      ? this.props.fillComponent
      : Fill;
    const HandleComponent = this.props.handleComponent
      ? this.props.handleComponent
      : Handle;

    return (
      <SliderContainer
        onMouseDown={this.handleMouseDown}
        onTouchStart={this.handleTouchStart}
        ref={(r) => {
          this.ref = r;
        }}
        disabled={this.props.disabled}
      >
        <HandleComponent
          onKeyDown={this.handleKeyDown}
          position={position}
          sliderWidth={this.state.width}
          active={this.state.active}
        />
        <FillComponent end={position} />
      </SliderContainer>
    );
  }
}
